import {
  collection,
  getDocs,
  orderBy,
  query,
  where,
  type QueryConstraint,
} from 'firebase/firestore'
import { firestoreDb } from './firebase'
import { callable } from './callable'
import { isEduDemoWorkspace } from './eduDemo/eduDemoWorkspaces.js'
import { demoListLeaveForClass } from './eduDemo/eduDemoDirectReads.js'
import { scopedReadConstraints } from './accessScopeService'
import type {
  EduLeaveRelation,
  EduLeaveRequest,
  EduLeaveStatus,
} from '../types/schema.js'

export type { EduLeaveRelation, EduLeaveRequest, EduLeaveStatus }

export interface RequestEduLeaveInput {
  workspaceId: string
  learnerId: string
  fromDate: string
  toDate: string
  reason: string
}

export interface RequestEduLeaveResult {
  requestId: string
  status: 'PENDING'
}

export interface CancelEduLeaveInput {
  workspaceId: string
  requestId: string
}

export interface CancelEduLeaveResult {
  requestId: string
  status: 'CANCELLED'
}

export interface DecideEduLeaveInput {
  workspaceId: string
  requestId: string
  action: 'APPROVE' | 'DECLINE' | 'REVOKE'
  note?: string
}

export interface DecideEduLeaveResult {
  requestId: string
  status: 'APPROVED' | 'DECLINED' | 'REVOKED'
  affectedAttendanceCount?: number
}

export interface ListFamilyLeaveInput {
  workspaceId: string
  learnerId: string
}

export interface ListFamilyLeaveResult {
  requests: EduLeaveRequest[]
}

export interface ListLeaveForClassInput {
  workspaceId: string
  classId: string
  status?: EduLeaveStatus
}

export type EduLeaveRequestDoc = EduLeaveRequest & { id: string }

export async function requestEduLeave(
  input: RequestEduLeaveInput,
): Promise<RequestEduLeaveResult> {
  return callable<RequestEduLeaveInput, RequestEduLeaveResult>('requestEduLeave', input)
}

export async function cancelEduLeave(
  input: CancelEduLeaveInput,
): Promise<CancelEduLeaveResult> {
  return callable<CancelEduLeaveInput, CancelEduLeaveResult>('cancelEduLeave', input)
}

export async function decideEduLeave(
  input: DecideEduLeaveInput,
): Promise<DecideEduLeaveResult> {
  return callable<DecideEduLeaveInput, DecideEduLeaveResult>('decideEduLeave', input)
}

export async function listFamilyLeave(
  input: ListFamilyLeaveInput,
): Promise<ListFamilyLeaveResult> {
  return callable<ListFamilyLeaveInput, ListFamilyLeaveResult>('listFamilyLeave', input)
}

export async function listLeaveForClass(
  input: ListLeaveForClassInput,
): Promise<EduLeaveRequestDoc[]> {
  const { workspaceId, classId, status } = input
  if (!workspaceId || !classId) {
    return []
  }

  if (isEduDemoWorkspace(workspaceId)) {
    return demoListLeaveForClass(input)
  }

  const scoped = scopedReadConstraints(workspaceId)
  const constraints: QueryConstraint[] = [
    where('workspaceId', '==', workspaceId),
    where('classIds', 'array-contains', classId),
  ]

  if (status) {
    constraints.push(where('status', '==', status))
  }

  constraints.push(...scoped)
  constraints.push(orderBy('fromDate', 'asc'))

  const q = query(collection(firestoreDb, 'edu_leave_requests'), ...constraints)
  const snap = await getDocs(q)
  return snap.docs.map((d) => ({
    id: d.id,
    ...(d.data() as object),
  } as EduLeaveRequestDoc))
}
